type Position = { x: number; y: number };

function getEventPosition(event: MouseEvent | TouchEvent): Position | null {
  const touchEvent = window.TouchEvent;
  if (touchEvent && event instanceof touchEvent) {
    if (event.touches.length > 0) {
      const touch = event.touches[0];
      return {
        x: touch.pageX,
        y: touch.pageY,
      };
    }
  } else if (event instanceof MouseEvent) {
    return {
      x: event.offsetX,
      y: event.offsetY,
    };
  } else {
    console.warn(`Unexpected event: ${event}`);
  }
  return null;
}

export class CustomMouseEvent {
  private _rawEvent: MouseEvent | TouchEvent;
  private _isAccepted = false;

  constructor(
    event: MouseEvent | TouchEvent,
    private _lastEvent: MouseEvent | TouchEvent | null,
    public isMouseDown: boolean
  ) {
    this._rawEvent = event;
  }

  accept() {
    this._isAccepted = true;
    this._rawEvent.preventDefault();
  }

  isAccepted() {
    return this._isAccepted;
  }

  get metaKey() {
    return this._rawEvent.metaKey;
  }

  get position(): Position {
    const position = getEventPosition(this._rawEvent);
    if (position != null) {
      return position;
    }

    // touchup typically doesn't contain touches so you
    // need the last event to know where it is
    if (this._lastEvent) {
      const backup = getEventPosition(this._lastEvent);
      if (backup != null) {
        return backup;
      }
    }

    return { x: 0, y: 0 };
  }

  get x() {
    return this.position.x;
  }

  get y() {
    return this.position.y;
  }
}

export interface MouseBehavior {
  down(e: CustomMouseEvent): void;
  move(e: CustomMouseEvent): void;
  up(e: CustomMouseEvent): void;
}

export class MouseBehaviorManager {
  private _mouseBehaviors: Array<MouseBehavior> = [];
  private _draggingBehavior: MouseBehavior | null = null;
  private _isMouseDown = false;
  private _lastEvent: MouseEvent | TouchEvent | null = null;

  public register(behavior: MouseBehavior) {
    this._mouseBehaviors.push(behavior);
  }

  public clear() {
    this._mouseBehaviors = [];
  }

  public listenOnElement(element: HTMLElement) {
    element.addEventListener("mousedown", (e: MouseEvent) => {
      this._notifyDown(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("mousemove", (e: MouseEvent) => {
      this._notifyMove(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("mouseup", (e: MouseEvent) => {
      this._notifyUp(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("mouseleave", (e: MouseEvent) => {
      this._notifyUp(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("touchstart", (e: TouchEvent) => {
      this._notifyDown(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("touchmove", (e: TouchEvent) => {
      this._notifyMove(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });

    element.addEventListener("touchend", (e: TouchEvent) => {
      this._notifyUp(
        new CustomMouseEvent(e, this._lastEvent, this._isMouseDown)
      );
      this._lastEvent = e;
    });
  }

  public _notifyDown(e: CustomMouseEvent) {
    this._isMouseDown = true;

    for (let behavior of this._mouseBehaviors) {
      behavior.down(e);
      if (e.isAccepted()) {
        this._draggingBehavior = behavior;
        return;
      }
    }
  }

  public _notifyUp(e: CustomMouseEvent) {
    if (this._draggingBehavior) {
      this._draggingBehavior.up(e);
      if (e.isAccepted()) {
        return;
      }
    }

    for (let behavior of this._mouseBehaviors) {
      if (behavior == this._draggingBehavior) {
        continue;
      }

      behavior.up(e);
      if (e.isAccepted()) {
        return;
      }
    }

    this._isMouseDown = false;
  }

  public _notifyMove(e: CustomMouseEvent) {
    if (this._draggingBehavior) {
      this._draggingBehavior.move(e);
      if (e.isAccepted()) {
        return;
      }
    }

    for (let behavior of this._mouseBehaviors) {
      if (behavior == this._draggingBehavior) {
        continue;
      }

      behavior.move(e);
      if (e.isAccepted()) {
        return;
      }
    }
  }
}
